// v0.3.0 — Multi-company (multi-yard) context.
//
// Holds which company (yard) the user is currently operating in, and the list
// of companies they may access. Mirrors the cache/circuit-breaker discipline of
// permissions.ts (see the 169M-request incident note there) so a failing
// my_yards RPC can never turn into a render-loop request storm.
//
// Security model: RLS (migration 037, has_yard) is the hard boundary — a user
// can only ever touch yards they belong to. The "active yard" here is UX
// scoping on top of that: which single company's data the pages show/insert.
// Reads add .eq('yard_id', getActiveYardId()); inserts set yard_id.

import { useEffect, useState } from 'react';
import { supabase } from './supabase';

export interface Yard {
  yard_id: number;
  code: string;
  name: string;
  is_default: boolean;
}

const ACTIVE_KEY = 'sega.active_yard_id';

// ---------------------------------------------------------------------------
// my_yards cache (same circuit breaker shape as permissions.ts)
// ---------------------------------------------------------------------------
let cache: Yard[] | null = null;
let inflight: Promise<Yard[]> | null = null;
let failureCount = 0;
let nextRetryAt = 0;
const MAX_BACKOFF_MS = 30_000;
const MAX_FAILURES = 5;
const LOCKOUT_MS = 5 * 60_000;

function nextBackoff(): number {
  if (failureCount >= MAX_FAILURES) return LOCKOUT_MS;
  return Math.min(1000 * Math.pow(2, failureCount), MAX_BACKOFF_MS);
}

export async function loadMyYards(): Promise<Yard[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  if (Date.now() < nextRetryAt) return []; // circuit open — no RPC
  inflight = (async () => {
    try {
      const { data, error } = await supabase.rpc('my_yards');
      if (error) throw error;
      const yards = ((data as Yard[]) ?? []).map((y) => ({
        yard_id: Number(y.yard_id),
        code: y.code,
        name: y.name,
        is_default: !!y.is_default,
      }));
      cache = yards;
      failureCount = 0;
      nextRetryAt = 0;
      reconcileActiveYard(yards);
      return yards;
    } catch (e) {
      failureCount += 1;
      const backoff = nextBackoff();
      nextRetryAt = Date.now() + backoff;
      console.warn(`my_yards fetch failed (attempt ${failureCount}/${MAX_FAILURES}). Backing off ${backoff}ms.`, e);
      return [];
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Clear both the yard list cache and the stored active yard (call on logout). */
export function clearYardState() {
  cache = null;
  inflight = null;
  failureCount = 0;
  nextRetryAt = 0;
  try { localStorage.removeItem(ACTIVE_KEY); } catch { /* noop */ }
}

// ---------------------------------------------------------------------------
// Active yard (persisted per browser)
// ---------------------------------------------------------------------------
export function getActiveYardId(): number | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeActiveYardId(id: number) {
  try { localStorage.setItem(ACTIVE_KEY, String(id)); } catch { /* noop */ }
}

/**
 * Ensure the stored active yard is still one the user belongs to.
 * If unset or stale, fall back to the default (or first) yard.
 */
function reconcileActiveYard(yards: Yard[]) {
  if (yards.length === 0) return;
  const current = getActiveYardId();
  if (current && yards.some((y) => y.yard_id === current)) return;
  const def = yards.find((y) => y.is_default) ?? yards[0];
  writeActiveYardId(def.yard_id);
}

/**
 * Switch the active company. Clears any in-progress cart (it may hold another
 * company's customer/lines) and hard-navigates so every page re-queries with
 * the new yard — no stale in-memory data can bleed across companies.
 */
export function switchYard(id: number) {
  if (getActiveYardId() === id) return;
  writeActiveYardId(id);
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('sega.cart.v1.'))
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* noop */ }
  window.location.assign('/dashboard');
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------
export function useYards(): {
  yards: Yard[];
  activeId: number | null;
  active: Yard | null;
  loading: boolean;
} {
  const [yards, setYards] = useState<Yard[]>(cache ?? []);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let cancelled = false;
    loadMyYards().then((y) => {
      if (!cancelled) {
        setYards(y);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const activeId = getActiveYardId();
  const active = yards.find((y) => y.yard_id === activeId) ?? null;
  return { yards, activeId, active, loading };
}
